import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import Swal from 'sweetalert2';

interface IoTDevice {
    id: string;
    name: string;
    device_type: string;
    manufacturer: string;
    model: string;
    serial_number: string;
    firmware_version: string;
    installation_date: string;
    last_maintenance: string;
    next_maintenance: string;
    status: string;
    department: string;
    room_number: string;
    assigned_staff_name?: string;
    configuration: Record<string, any>;
    created_at: string;
}

interface DeviceStats {
    total_devices: number;
    active_devices: number;
    offline_devices: number;
    maintenance_devices: number;
    error_devices: number;
}

interface Props {
    onBack: () => void;
}

const IoTCenter: React.FC<Props> = ({ onBack }) => {
    const baseUrl = useBaseUrl();
    const [devices, setDevices] = useState<IoTDevice[]>([]);
    const [stats, setStats] = useState<DeviceStats>({
        total_devices: 0,
        active_devices: 0,
        offline_devices: 0,
        maintenance_devices: 0,
        error_devices: 0
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedType, setSelectedType] = useState<string>('all');
    const [selectedStatus, setSelectedStatus] = useState<string>('all');
    const [selectedLocation, setSelectedLocation] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(12);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [totalCount, setTotalCount] = useState<number>(0);

    const deviceTypes = [
        { value: 'monitor', label: 'Patient Monitor', color: '#007bff', icon: 'bx-heart' },
        { value: 'sensor', label: 'Sensor', color: '#28a745', icon: 'bx-wifi' },
        { value: 'pump', label: 'Infusion Pump', color: '#fd7e14', icon: 'bx-droplet' },
        { value: 'ventilator', label: 'Ventilator', color: '#dc3545', icon: 'bx-wind' },
        { value: 'scanner', label: 'Scanner', color: '#6610f2', icon: 'bx-scan' },
        { value: 'analyzer', label: 'Analyzer', color: '#17a2b8', icon: 'bx-test-tube' }
    ];

    const statusOptions = [
        { value: 'online', label: 'Online', color: '#28a745', icon: 'bx-wifi' },
        { value: 'offline', label: 'Offline', color: '#6c757d', icon: 'bx-wifi-off' },
        { value: 'maintenance', label: 'Maintenance', color: '#ffc107', icon: 'bx-wrench' },
        { value: 'error', label: 'Error', color: '#dc3545', icon: 'bx-error' }
    ];

    useEffect(() => {
        loadDevices();
        loadStats();
    }, [selectedType, selectedStatus, selectedLocation]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight || loading || !hasMore) {
                return;
            }
            loadMoreDevices();
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loading, hasMore]);

    const loadStats = async () => {
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            const response = await axios.get(`${baseUrl}/resources/api/v1/iot-devices/stats/`, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error loading device stats:', error);
        }
    };

    const loadDevices = async () => {
        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/iot-devices/`;
            const params = new URLSearchParams();
            
            if (selectedType !== 'all') params.append('device_type', selectedType);
            if (selectedStatus !== 'all') params.append('status', selectedStatus);
            if (selectedLocation !== 'all') params.append('location', selectedLocation);
            params.append('page_size', itemsPerPage.toString());

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                if (Array.isArray(response.data.results)) {
                    setDevices(response.data.results);
                    setTotalCount(response.data.count || 0);
                    setHasMore(!!response.data.next);
                } else if (Array.isArray(response.data)) {
                    setDevices(response.data);
                    setTotalCount(response.data.length);
                    setHasMore(false);
                }
                setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error loading IoT devices:', error);
            setDevices([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreDevices = async () => {
        if (loading || !hasMore) return;

        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/iot-devices/`;
            const params = new URLSearchParams();
            
            if (selectedType !== 'all') params.append('device_type', selectedType);
            if (selectedStatus !== 'all') params.append('status', selectedStatus);
            if (selectedLocation !== 'all') params.append('location', selectedLocation);
            params.append('page_size', itemsPerPage.toString());
            params.append('page', (currentPage + 1).toString());

            url += `?${params.toString()}`;

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && Array.isArray(response.data.results)) {
                setDevices(prev => [...prev, ...response.data.results]);
                setHasMore(!!response.data.next);
                setCurrentPage(prev => prev + 1);
            }
        } catch (error) {
            console.error('Error loading more devices:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTypeInfo = (type: string) => {
        return deviceTypes.find(t => t.value === type) || { label: type, color: '#6c757d', icon: 'bx-chip' };
    };

    const getStatusInfo = (status: string) => {
        return statusOptions.find(s => s.value === status) || { label: status, color: '#6c757d', icon: 'bx-info-circle' };
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const isMaintenanceDue = (nextMaintenanceDate: string) => {
        const nextDate = new Date(nextMaintenanceDate);
        const today = new Date();
        const diffDays = Math.ceil((nextDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
        return diffDays <= 7; // Maintenance due within 7 days
    };

    const viewDevice = (device: IoTDevice) => {
        const typeInfo = getTypeInfo(device.device_type);
        const statusInfo = getStatusInfo(device.status);
        const maintenanceDue = isMaintenanceDue(device.next_maintenance);
        
        Swal.fire({
            title: device.name,
            html: `
                <div class="text-start">
                    <div class="d-flex justify-content-between mb-3">
                        <span class="badge" style="background-color: ${typeInfo.color}">
                            <i class="${typeInfo.icon} me-1"></i>
                            ${typeInfo.label}
                        </span>
                        <span class="badge" style="background-color: ${statusInfo.color}">
                            <i class="${statusInfo.icon} me-1"></i>
                            ${statusInfo.label}
                        </span>
                    </div>
                    
                    <div class="row mb-3">
                        <div class="col-6">
                            <p><strong>Serial Number:</strong> ${device.serial_number}</p>
                            <p><strong>Manufacturer:</strong> ${device.manufacturer}</p>
                            <p><strong>Model:</strong> ${device.model}</p>
                        </div>
                        <div class="col-6">
                            <p><strong>Firmware:</strong> ${device.firmware_version}</p>
                            <p><strong>Location:</strong> ${device.department} - ${device.room_number}</p>
                            <p><strong>Installed:</strong> ${formatDate(device.installation_date)}</p>
                            ${device.assigned_staff_name ? `<p><strong>Assigned to:</strong> ${device.assigned_staff_name}</p>` : ''}
                        </div>
                    </div>
                    
                    <div class="mb-3">
                        <strong>Maintenance Schedule:</strong>
                        <div class="mt-1">
                            <small class="d-block">Last: ${formatDate(device.last_maintenance)}</small>
                            <small class="d-block ${maintenanceDue ? 'text-warning' : ''}">
                                Next: ${formatDate(device.next_maintenance)}
                                ${maintenanceDue ? ' <span class="badge bg-warning text-dark">Due Soon</span>' : ''}
                            </small>
                        </div>
                    </div>
                    
                    ${device.configuration && Object.keys(device.configuration).length > 0 ? `
                        <div class="mb-3">
                            <strong>Configuration:</strong>
                            <div class="mt-1">
                                ${Object.entries(device.configuration).map(([key, value]) => 
                                    `<small class="d-block"><strong>${key}:</strong> ${value}</small>`
                                ).join('')}
                            </div>
                        </div>
                    ` : ''}
                    
                    <div class="mt-3 pt-3 border-top">
                        <small class="text-muted">
                            Device registered: ${formatDate(device.created_at)}
                        </small>
                    </div>
                </div>
            `,
            width: '600px',
            showCloseButton: true,
            showCancelButton: device.status === 'online',
            confirmButtonText: device.status === 'error' ? 'Report Issue' : 'Close',
            cancelButtonText: device.status === 'online' ? 'Remote Control' : undefined,
            customClass: {
                htmlContainer: 'text-start'
            }
        }).then((result) => {
            if (result.isConfirmed && device.status === 'error') {
                handleReportIssue(device);
            } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
                handleRemoteControl(device);
            }
        });
    };

    const handleReportIssue = (device: IoTDevice) => {
        Swal.fire({
            title: 'Report Device Issue',
            html: `
                <div class="text-start">
                    <p>Report an issue with <strong>${device.name}</strong></p>
                    <div class="mb-3">
                        <label class="form-label">Issue Description</label>
                        <textarea id="issueDescription" class="form-control" rows="4" placeholder="Describe the issue..."></textarea>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Priority Level</label>
                        <select id="issuePriority" class="form-select">
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                        </select>
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Submit Report',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const description = (document.getElementById('issueDescription') as HTMLTextAreaElement)?.value;
                const priority = (document.getElementById('issuePriority') as HTMLSelectElement)?.value;
                
                if (!description) {
                    Swal.showValidationMessage('Please describe the issue');
                    return false;
                }
                
                return { description, priority };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire('Success!', 'Issue report has been submitted to the maintenance team.', 'success');
            }
        });
    };

    const handleRemoteControl = (device: IoTDevice) => {
        Swal.fire({
            title: 'Remote Device Control',
            html: `
                <div class="text-start">
                    <p>Remote control options for <strong>${device.name}</strong></p>
                    <div class="d-grid gap-2">
                        <button class="btn btn-outline-primary" onclick="window.deviceAction('restart')">
                            <i class="bx bx-refresh me-2"></i>Restart Device
                        </button>
                        <button class="btn btn-outline-info" onclick="window.deviceAction('diagnostics')">
                            <i class="bx bx-analyze me-2"></i>Run Diagnostics
                        </button>
                        <button class="btn btn-outline-warning" onclick="window.deviceAction('calibrate')">
                            <i class="bx bx-adjust me-2"></i>Calibrate
                        </button>
                        <button class="btn btn-outline-secondary" onclick="window.deviceAction('logs')">
                            <i class="bx bx-file me-2"></i>View Logs
                        </button>
                    </div>
                </div>
            `,
            showCloseButton: true,
            showConfirmButton: false,
            didOpen: () => {
                (window as any).deviceAction = (action: string) => {
                    Swal.fire('Command Sent', `${action} command sent to ${device.name}`, 'success');
                };
            },
            willClose: () => {
                delete (window as any).deviceAction;
            }
        });
    };

    const getUniqueLocations = () => {
        const locations = devices.map(device => `${device.department} - ${device.room_number}`);
        return Array.from(new Set(locations)).filter(Boolean).sort();
    };

    const filteredDevices = devices.filter(device =>
        device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.serial_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.manufacturer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.room_number.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="container-fluid p-4">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-1">
                                <i className="bx bx-chip me-2 text-primary"></i>
                                IoT Device Management
                            </h4>
                            <p className="text-muted mb-0">Monitor and manage connected medical devices</p>
                        </div>
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={onBack}
                        >
                            <i className="bx bx-arrow-back me-2"></i>
                            Back to Resources
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="row mb-4">
                <div className="col-md-2-4 col-sm-6 mb-3">
                    <div className="card stat-card border-0 bg-primary text-white">
                        <div className="card-body text-center">
                            <i className="bx bx-chip display-6 mb-2"></i>
                            <h3 className="mb-1">{stats.total_devices}</h3>
                            <small>Total Devices</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-2-4 col-sm-6 mb-3">
                    <div className="card stat-card border-0 bg-success text-white">
                        <div className="card-body text-center">
                            <i className="bx bx-wifi display-6 mb-2"></i>
                            <h3 className="mb-1">{stats.active_devices}</h3>
                            <small>Online</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-2-4 col-sm-6 mb-3">
                    <div className="card stat-card border-0 bg-secondary text-white">
                        <div className="card-body text-center">
                            <i className="bx bx-wifi-off display-6 mb-2"></i>
                            <h3 className="mb-1">{stats.offline_devices}</h3>
                            <small>Offline</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-2-4 col-sm-6 mb-3">
                    <div className="card stat-card border-0 bg-warning text-white">
                        <div className="card-body text-center">
                            <i className="bx bx-wrench display-6 mb-2"></i>
                            <h3 className="mb-1">{stats.maintenance_devices}</h3>
                            <small>Maintenance</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-2-4 col-sm-6 mb-3">
                    <div className="card stat-card border-0 bg-danger text-white">
                        <div className="card-body text-center">
                            <i className="bx bx-error display-6 mb-2"></i>
                            <h3 className="mb-1">{stats.error_devices}</h3>
                            <small>Error</small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-4">
                <div className="col-lg-3 col-md-6 mb-3">
                    <div className="position-relative">
                        <i className="bx bx-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                        <input
                            type="text"
                            className="form-control ps-5"
                            placeholder="Search devices..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                    <select
                        className="form-select"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                    >
                        <option value="all">All Types</option>
                        {deviceTypes.map(type => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                    <select
                        className="form-select"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        {statusOptions.map(status => (
                            <option key={status.value} value={status.value}>
                                {status.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-lg-3 col-md-6 mb-3">
                    <select
                        className="form-select"
                        value={selectedLocation}
                        onChange={(e) => setSelectedLocation(e.target.value)}
                    >
                        <option value="all">All Locations</option>
                        {getUniqueLocations().map(location => (
                            <option key={location} value={location}>
                                {location}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Results Summary */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">
                            Showing {filteredDevices.length} of {totalCount} devices
                        </span>
                    </div>
                </div>
            </div>

            {/* Devices Grid */}
            <div className="row">
                {filteredDevices.map(device => {
                    const typeInfo = getTypeInfo(device.device_type);
                    const statusInfo = getStatusInfo(device.status);
                    const maintenanceDue = isMaintenanceDue(device.next_maintenance);

                    return (
                        <div key={device.id} className="col-lg-4 col-md-6 mb-4">
                            <div className={`card h-100 device-card ${device.status === 'error' ? 'border-danger' : ''}`}>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span 
                                            className="badge" 
                                            style={{ backgroundColor: typeInfo.color }}
                                        >
                                            <i className={`${typeInfo.icon} me-1`}></i>
                                            {typeInfo.label}
                                        </span>
                                        <span 
                                            className="badge" 
                                            style={{ backgroundColor: statusInfo.color }}
                                        >
                                            <i className={`${statusInfo.icon} me-1`}></i>
                                            {statusInfo.label}
                                        </span>
                                    </div>
                                    
                                    <h6 className="card-title mb-2">{device.name}</h6>
                                    
                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            <i className="bx bx-id-card me-1"></i>
                                            Serial: {device.serial_number}
                                        </small>
                                        <small className="text-muted d-block">
                                            <i className="bx bx-building me-1"></i>
                                            {device.manufacturer} - {device.model}
                                        </small>
                                        <small className="text-muted d-block">
                                            <i className="bx bx-map me-1"></i>
                                            {device.department} - {device.room_number}
                                        </small>
                                        {device.assigned_staff_name && (
                                            <small className="text-muted d-block">
                                                <i className="bx bx-user me-1"></i>
                                                Assigned to: {device.assigned_staff_name}
                                            </small>
                                        )}
                                    </div>
                                    
                                    <div className="mb-3">
                                        <small className="text-muted d-block">
                                            <i className="bx bx-wrench me-1"></i>
                                            Last service: {formatDate(device.last_maintenance)}
                                        </small>
                                        <small className={`d-block ${maintenanceDue ? 'text-warning' : 'text-muted'}`}>
                                            <i className="bx bx-calendar me-1"></i>
                                            Next service: {formatDate(device.next_maintenance)}
                                            {maintenanceDue && <span className="ms-1">(Due Soon)</span>}
                                        </small>
                                    </div>
                                    
                                    <div className="d-flex gap-2">
                                        <button 
                                            className="btn btn-outline-primary btn-sm flex-grow-1"
                                            onClick={() => viewDevice(device)}
                                        >
                                            <i className="bx bx-info-circle me-1"></i>
                                            Details
                                        </button>
                                        
                                        {device.status === 'online' && (
                                            <button 
                                                className="btn btn-outline-success btn-sm"
                                                onClick={() => handleRemoteControl(device)}
                                            >
                                                <i className="bx bx-control me-1"></i>
                                                Control
                                            </button>
                                        )}
                                        
                                        {device.status === 'error' && (
                                            <button 
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => handleReportIssue(device)}
                                            >
                                                <i className="bx bx-error me-1"></i>
                                                Report
                                            </button>
                                        )}
                                    </div>
                                    
                                    {maintenanceDue && (
                                        <div className="mt-2">
                                            <small className="text-warning">
                                                <i className="bx bx-time me-1"></i>
                                                Maintenance Due Soon
                                            </small>
                                        </div>
                                    )}
                                    
                                    {device.status === 'offline' && (
                                        <div className="mt-2">
                                            <small className="text-danger">
                                                <i className="bx bx-power-off me-1"></i>
                                                Device Offline
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Loading State */}
            {loading && (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">Loading devices...</div>
                </div>
            )}

            {/* No Results */}
            {!loading && filteredDevices.length === 0 && (
                <div className="text-center py-5">
                    <i className="bx bx-chip display-4 text-muted"></i>
                    <h5 className="mt-3 text-muted">No devices found</h5>
                    <p className="text-muted">Try adjusting your filters or search terms.</p>
                </div>
            )}

            {/* Infinite Scroll Indicator */}
            {!loading && hasMore && filteredDevices.length > 0 && (
                <div className="text-center py-3">
                    <small className="text-muted">Scroll down to load more devices</small>
                </div>
            )}

            <style>{`
                .device-card {
                    transition: transform 0.2s, box-shadow 0.2s;
                    border: 1px solid #e3e6f0;
                }
                
                .device-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.15);
                }
                
                .stat-card {
                    transition: transform 0.2s;
                }
                
                .stat-card:hover {
                    transform: translateY(-2px);
                }
                
                .badge {
                    font-size: 0.75rem;
                }
                
                .card-title {
                    font-size: 1rem;
                    font-weight: 600;
                    line-height: 1.3;
                }
                
                @media (min-width: 1200px) {
                    .col-md-2-4 {
                        flex: 0 0 20%;
                        max-width: 20%;
                    }
                }
            `}</style>
        </div>
    );
};

export default IoTCenter;
